import type {
  CalendarAttendeeSuggestion,
  CalendarEventDeleteInput,
  CalendarEventsResponse,
  CalendarEventUpdateInput,
  CalendarEventWriteInput,
  CalendarExternalEvent,
  CalendarMeetingEvent,
  CalendarQueryParams,
  CalendarSuggestedTime,
  CalendarSuggestTimesInput,
  CalendarTask,
} from '@scrambl/shared'

import { apiClient } from './api-client'

// Helper function to normalize dates in calendar events
// Note: API returns dates as strings, need to convert to Date objects
const normalizeDates = <T>(event: T): T => {
  const result = { ...event } as Record<string, unknown>
  if ('start' in result && result.start) {
    result.start = new Date(result.start as string | Date)
  }
  if ('end' in result && result.end) {
    result.end = new Date(result.end as string | Date)
  }
  if ('updatedAt' in result && result.updatedAt) {
    result.updatedAt = new Date(result.updatedAt as string | Date)
  }
  return result as T
}

// Helper function to normalize calendar response
const normalizeCalendarResponse = (
  response: CalendarEventsResponse,
): CalendarEventsResponse => {
  return {
    ...response,
    tasks: response.tasks.map(task => normalizeDates(task)),
    events: response.events.map(event => normalizeDates(event)),
    externalEvents: response.externalEvents.map(event => normalizeDates(event)),
    meta: {
      ...response.meta,
      syncStatus: response.meta.syncStatus
        ? {
            googleCalendar: response.meta.syncStatus.googleCalendar
              ? {
                  ...response.meta.syncStatus.googleCalendar,
                  lastSyncAt: response.meta.syncStatus.googleCalendar.lastSyncAt
                    ? new Date(
                        response.meta.syncStatus.googleCalendar.lastSyncAt,
                      )
                    : undefined,
                }
              : undefined,
            outlookCalendar: response.meta.syncStatus.outlookCalendar
              ? {
                  ...response.meta.syncStatus.outlookCalendar,
                  lastSyncAt: response.meta.syncStatus.outlookCalendar
                    .lastSyncAt
                    ? new Date(
                        response.meta.syncStatus.outlookCalendar.lastSyncAt,
                      )
                    : undefined,
                }
              : undefined,
          }
        : undefined,
    },
  }
}

// Build query string from params
const buildQueryString = (params: CalendarQueryParams): string => {
  const searchParams = new URLSearchParams()

  searchParams.append('startDate', params.startDate)
  searchParams.append('endDate', params.endDate)

  if (params.includeTasks !== undefined) {
    searchParams.append('includeTasks', String(params.includeTasks))
  }

  if (params.includeExternalEvents !== undefined) {
    searchParams.append(
      'includeExternalEvents',
      String(params.includeExternalEvents),
    )
  }

  if (params.entityIds?.length) {
    searchParams.append('entityIds', params.entityIds.join(','))
  }

  if (params.projectIds?.length) {
    searchParams.append('projectIds', params.projectIds.join(','))
  }

  if (params.assigneeIds?.length) {
    searchParams.append('assigneeIds', params.assigneeIds.join(','))
  }

  if (params.priorities?.length) {
    searchParams.append('priorities', params.priorities.join(','))
  }

  if (params.statuses?.length) {
    searchParams.append('statuses', params.statuses.join(','))
  }

  if (params.tagIds?.length) {
    searchParams.append('tagIds', params.tagIds.join(','))
  }

  if (params.view) {
    searchParams.append('view', params.view)
  }

  return searchParams.toString()
}

/**
 * Calendar API client for fetching calendar events
 */
export const calendarApi = {
  /**
   * Get calendar events for a date range
   * Returns both tasks and external calendar events
   */
  getEvents: async (
    params: CalendarQueryParams,
  ): Promise<CalendarEventsResponse> => {
    const queryString = buildQueryString(params)
    const response = await apiClient<CalendarEventsResponse>(
      `/calendar/events?${queryString}`,
    )
    return normalizeCalendarResponse(response)
  },

  /**
   * Sync external calendars (Google, Outlook)
   * Triggers a fresh sync of external calendar data
   */
  syncExternalCalendars: async (): Promise<{
    success: boolean
    message: string
  }> => {
    return apiClient('/calendar/sync', { method: 'POST' })
  },

  createGoogleEvent: async (
    body: CalendarEventWriteInput,
  ): Promise<CalendarExternalEvent> => {
    const response = await apiClient<
      CalendarExternalEvent,
      CalendarEventWriteInput
    >('/calendar/google/events', { method: 'POST', body })
    return normalizeDates(response)
  },

  updateGoogleEvent: async (
    body: CalendarEventUpdateInput,
  ): Promise<CalendarExternalEvent> => {
    const response = await apiClient<
      CalendarExternalEvent,
      CalendarEventUpdateInput
    >('/calendar/google/events', { method: 'PATCH', body })
    return normalizeDates(response)
  },

  deleteGoogleEvent: async (
    body: CalendarEventDeleteInput,
  ): Promise<{ success: true }> => {
    return apiClient<{ success: true }, CalendarEventDeleteInput>(
      '/calendar/google/events',
      { method: 'DELETE', body },
    )
  },

  createOutlookEvent: async (
    body: CalendarEventWriteInput,
  ): Promise<CalendarExternalEvent> => {
    const response = await apiClient<
      CalendarExternalEvent,
      CalendarEventWriteInput
    >('/calendar/outlook/events', { method: 'POST', body })
    return normalizeDates(response)
  },

  updateOutlookEvent: async (
    body: CalendarEventUpdateInput,
  ): Promise<CalendarExternalEvent> => {
    const response = await apiClient<
      CalendarExternalEvent,
      CalendarEventUpdateInput
    >('/calendar/outlook/events', { method: 'PATCH', body })
    return normalizeDates(response)
  },

  deleteOutlookEvent: async (
    body: CalendarEventDeleteInput,
  ): Promise<{ success: true }> => {
    return apiClient<{ success: true }, CalendarEventDeleteInput>(
      '/calendar/outlook/events',
      { method: 'DELETE', body },
    )
  },

  suggestGoogleEventTimes: async (
    body: CalendarSuggestTimesInput,
  ): Promise<{ suggestions: CalendarSuggestedTime[] }> => {
    return apiClient<
      { suggestions: CalendarSuggestedTime[] },
      CalendarSuggestTimesInput
    >('/calendar/google/suggest-times', { method: 'POST', body })
  },

  searchAttendeeSuggestions: async (
    query: string,
    limit = 12,
  ): Promise<{ suggestions: CalendarAttendeeSuggestion[] }> => {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
    })
    return apiClient<{ suggestions: CalendarAttendeeSuggestion[] }>(
      `/calendar/attendee-suggestions?${params.toString()}`,
    )
  },
}

// Export types for convenience
export type {
  CalendarEventsResponse,
  CalendarMeetingEvent,
  CalendarTask,
  CalendarExternalEvent,
}
